import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, BookOpen, MessageSquare, Users, Star, Newspaper, CalendarClock } from "lucide-react";
import { authFetch } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import AdminLoading from "../../components/admin/AdminLoading";
import { toast } from "sonner";

const emptyStats = {
  stages: 0,
  bookings: 0,
  contacts: 0,
  leads: 0,
  testimonials: 0,
  posts: 0,
  confirmed: 0,
  open_slots: 0,
  today_bookings: 0,
  unread_bookings: 0,
  unread_leads: 0,
  unread_contacts: 0,
  inbox: [],
};

function formatWhen(dateStr) {
  if (!dateStr) return "";
  try {
    return new Date(dateStr).toLocaleString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return dateStr;
  }
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(emptyStats);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadStats = async () => {
      try {
        const res = await authFetch("/api/admin/stats");
        if (!res.ok) {
          toast.error("Impossible de charger les statistiques");
          return;
        }
        const data = await res.json();
        setStats({ ...emptyStats, ...data, inbox: Array.isArray(data.inbox) ? data.inbox : [] });
      } catch {
        toast.error("Erreur lors du chargement du dashboard");
      } finally {
        setLoading(false);
      }
    };

    loadStats();
  }, []);

  const unreadTotal = (stats.unread_bookings || 0) + (stats.unread_contacts || 0);

  const cards = [
    {
      label: "Réservations",
      value: stats.bookings,
      unread: stats.unread_bookings,
      icon: BookOpen,
      path: "/admin/bookings",
    },
    {
      label: "Leads panier",
      value: stats.leads,
      unread: stats.unread_leads,
      icon: Users,
      path: "/admin/bookings",
    },
    {
      label: "Contacts",
      value: stats.contacts,
      unread: stats.unread_contacts,
      icon: MessageSquare,
      path: "/admin/contacts",
    },
    { label: "Stages", value: stats.stages, icon: Calendar, path: "/admin/stages" },
    { label: "Avis", value: stats.testimonials, icon: Star, path: "/admin/testimonials" },
    { label: "Blog", value: stats.posts, icon: Newspaper, path: "/admin/blog" },
  ];

  return (
    <div>
      <div className="mb-7">
        <h1 className="text-[1.35rem] font-semibold text-slate-900 tracking-tight">Dashboard</h1>
        <span className="section-title-rule" aria-hidden="true" />
        <p className="text-slate-500 text-sm -mt-1">
          Bienvenue, {user?.full_name || user?.email}
        </p>
      </div>

      {loading ? (
        <AdminLoading message="Chargement des statistiques..." />
      ) : (
        <>
          <section
            className="mb-5 rounded-lg border border-slate-200 bg-white overflow-hidden"
            aria-labelledby="inbox-title"
          >
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-3">
              <div>
                <h2 id="inbox-title" className="text-sm font-semibold text-slate-900">
                  À traiter
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  {unreadTotal === 0
                    ? "Rien de nouveau — tout est à jour."
                    : `${unreadTotal} élément${unreadTotal > 1 ? "s" : ""} non lu${unreadTotal > 1 ? "s" : ""}`}
                </p>
              </div>
              {unreadTotal > 0 ? (
                <span className="inline-flex items-center rounded-full bg-[rgba(58,0,224,0.08)] text-[#3a00e0] text-xs font-semibold px-2.5 py-1 tabular-nums">
                  {unreadTotal} non lu{unreadTotal > 1 ? "s" : ""}
                </span>
              ) : null}
            </div>
            {unreadTotal === 0 ? (
              <p className="px-4 py-6 text-sm text-slate-500">
                Les nouvelles réservations, leads et messages apparaîtront ici.
              </p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {stats.inbox.map((item) => (
                  <li key={`${item.kind}-${item.id}`}>
                    <Link
                      to={item.href}
                      className="flex items-start justify-between gap-3 px-4 py-3 hover:bg-slate-50 transition-colors"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">
                          <span
                            className="inline-block w-1.5 h-1.5 rounded-full bg-[#3a00e0] mr-2 align-middle"
                            aria-hidden
                          />
                          {item.title}
                        </p>
                        <p className="text-xs text-slate-500 truncate mt-0.5 pl-3.5">{item.subtitle}</p>
                      </div>
                      <span className="text-[11px] text-slate-400 whitespace-nowrap tabular-nums">
                        {formatWhen(item.created_at)}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <div className="admin-metrics">
            {cards.map(({ label, value, unread, icon: Icon, path }) => (
              <Link key={label} to={path} className="admin-metric group">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <p className="text-[13px] text-slate-500 group-hover:text-slate-700 transition-colors">{label}</p>
                  <Icon className="h-4 w-4 text-[#3a00e0] shrink-0" />
                </div>
                <p className="text-[1.75rem] font-semibold text-slate-900 tabular-nums tracking-tight leading-none">
                  {value}
                </p>
                {unread > 0 ? (
                  <p className="mt-2 text-xs font-medium text-[#3a00e0] tabular-nums">
                    {unread} non lu{unread > 1 ? "s" : ""}
                  </p>
                ) : (
                  <p className="mt-2 text-xs text-slate-400">À jour</p>
                )}
              </Link>
            ))}
          </div>

          <div className="mt-5 rounded-lg border border-slate-200 bg-white px-4 py-3.5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-8 text-sm">
            <p className="text-slate-600">
              <span className="font-semibold text-slate-900 tabular-nums">{stats.today_bookings}</span>
              {" "}réservation{stats.today_bookings === 1 ? "" : "s"} aujourd&apos;hui
            </p>
            <p className="text-slate-600">
              <span className="font-semibold text-slate-900 tabular-nums">{stats.confirmed}</span>
              {" "}confirmée{stats.confirmed === 1 ? "" : "s"}
            </p>
            <p className="text-slate-600 flex items-center gap-1.5">
              <CalendarClock className="h-3.5 w-3.5 text-[#3a00e0]" aria-hidden />
              <span className="font-semibold text-slate-900 tabular-nums">{stats.open_slots}</span>
              {" "}créneau{stats.open_slots === 1 ? "" : "x"} ouvert{stats.open_slots === 1 ? "" : "s"}
            </p>
            {stats.open_slots === 0 ? (
              <Link to="/admin/slots" className="text-[#3a00e0] font-medium hover:underline sm:ml-auto">
                Ajouter un créneau
              </Link>
            ) : (
              <Link to="/admin/slots" className="text-slate-500 hover:text-[#3a00e0] hover:underline sm:ml-auto">
                Voir les créneaux
              </Link>
            )}
          </div>
        </>
      )}
    </div>
  );
}
